import React, { useState } from "react";
import {
  Button,
  useMediaQuery,
  TableRow,
  TableCell,
  Table,
  TableBody,
  Alert,
} from "@mui/material";
import Grid from '@mui/material/Grid2';
import _ from "lodash";

// Import CSS
import "./CommitteeManagement.scss";

// Zustand store
import { useAppStore } from "../../stores";

// generic components
import Dropdown from "../../components/Dropdown/Dropdown";
import GenericModal from "../../components/GenericModal/GenericModal";
import GenericSpinner from "../../components/GenericSpinner/GenericSpinner";

// Import redux operations
import MemberSearch from "../../components/MemberSearch/MemberSearch";
import ConfirmActionModal from "components/ConfirmActionModal/ConfirmActionModal";
import { useSnackbar } from "notistack";
import axios from "axios";
import MUIDataTableWrapper from "../../components/MUIDataTableWrapper/MUIDataTableWrapper";

interface Props {}

interface committeeTypesItem {
  committeeTypeId: number;
  committeeName: string;
  isSpecialCommittee: boolean;
}

// cancel request if need be
const CancelToken = axios.CancelToken;
let cancel: any;

const CommitteeManagement: React.FC<Props> = () => {
  const { enqueueSnackbar } = useSnackbar();

  const displayMobile = useMediaQuery("(max-width:959px)");

  // Zustand store
  const committeeAssignments = useAppStore(
    (state) => state.committees.committeeAssignments
  );
  const committeeTypes = useAppStore(
    (state) => state.committees.committeeTypes
  );
  const currentSelectedCommittee = useAppStore(
    (state) => state.committees.currentSelectedCommittee
  );
  const memberSearchResult = useAppStore(
    (state) => state.search.memberSearchResult
  );
  const setCommitteeTypes = useAppStore((state) => state.setCommitteeTypes);
  const setCommitteeAssignments = useAppStore((state) => state.setCommitteeAssignments);
  const updateCurrentCommitteeType = useAppStore((state) => state.updateCurrentCommitteeType);

  // inital dropdown that loads
  const [committeeDropDownSelection, setCommitteeDropdownSelection] = useState(
    ""
  );

  const [
    committeeDropDownSelectionBulkAction,
    setCommitteeDropdownSelectionBulkAction,
  ] = useState("");

  const formatCommitteeTypes = (committeeTypes: any) => {
    let newCommitteeTypes = [""];
    return newCommitteeTypes.concat(committeeTypes);
  };

  const [initialLoadPending, setInitialLoadPending] = React.useState(false);

  React.useEffect(() => {
    setInitialLoadPending(true);
    axios
      .get("/api/Committees/committeeTypes", {
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
      })
      .then((response) => {
        if (response && response.data) {
          const { data } = response;
          if (data.length === 0) {
            enqueueSnackbar(
              "No committees are available for this convention!",
              {
                variant: "error",
              }
            );
          } else {
            setCommitteeTypes(formatCommitteeTypes(response.data));
          }
        }
        setInitialLoadPending(false);
      })
      .catch((error: any) => {
        if (axios.isCancel(error)) {
          console.warn("Error: " + error.message);
        } else {
          enqueueSnackbar("The following error occured: " + error, {
            variant: "error",
          });
          setInitialLoadPending(false);
        }
      });
    return () => {
      setCommitteeTypes([]);
      cancel("Request has been cancelled");
    };
  }, []);

  // handling bulk actions
  const [bulkActionSelection, setBulkActionSelection] = useState("");

  const bulkActionOnChange = (e: any) => {
    const { value } = e.target;
    // clear committee dropdown for bulk action
    setCommitteeDropdownSelectionBulkAction("");
    setBulkActionSelection(value);
  };

  const handleCommitteeTypeDropdownChange = (
    event: { target: { value: string } }
  ) => {
    const { value } = event.target;
    setBulkActionSelection("");
    setCommitteeDropdownSelectionBulkAction("");
    setSelectedRowIndexes([]);
    setCommitteeDropdownSelection(value);
    if (value !== "") {
      setCommitteeAssignmentsRequestPending(true);
      axios
        .get(`/api/Committees/delegatesForComitteeType/${value}`, {
          cancelToken: new CancelToken(function executor(c) {
            // An executor function receives a cancel function as a parameter
            cancel = c;
          }),
        })
        .then((response) => {
          if (response && response.data) {
            const { data } = response;
            if (data.currentCommittee === null) {
              enqueueSnackbar("Could not load the requested committee!", {
                variant: "error",
              });
            } else {
              updateCurrentCommitteeType(data.currentCommittee);
              setCommitteeAssignments(data.committeeAssignments);
            }
          }
          setCommitteeAssignmentsRequestPending(false);
        })
        .catch((error) => {
          console.warn(error);
          if (axios.isCancel(error)) {
            console.warn("Error: " + error.message);
          } else {
            console.warn(error);
            setCommitteeAssignmentsRequestPending(false);
            enqueueSnackbar(
              "Please reload the page! The following error occured: " + error,
              {
                variant: "error",
              }
            );
          }
        });
    }
  };

  const handleCommitteeTypeDropdownChangeBulkAction = (
    event: { target: { value: string } }
  ) => {
    const { value } = event.target;
    setCommitteeDropdownSelectionBulkAction(value);
  };

  const [open, setOpen] = React.useState(false);

  const [
    committeeAssignmentsRequestPending,
    setCommitteeAssignmentsRequestPending,
  ] = React.useState(false);

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const confirmActionModalOnClose = () => {
    setBulkActionSelection("");
    setConfirmationModalOpen(false);
  };

  // Handle operations for the main data table
  const [selectedRowIndexes, setSelectedRowIndexes]: any = useState([]);

  const applyButtonOnClickModalPopup = () => {
    let allActionsSatisfied = false;
    if (selectedRowIndexes.length > 0) {
      if (bulkActionSelection === "moveMember") {
        if (committeeDropDownSelectionBulkAction !== "") {
          allActionsSatisfied = true;
        }
      } else {
        allActionsSatisfied = true;
      }
      if (allActionsSatisfied) {
        setConfirmationModalOpen(true);
        return;
      }
    } else {
      enqueueSnackbar(
        "You need to select at least one record from the table below",
        {
          variant: "error",
        }
      );
    }
  };

  const findCorrespondingAttendeeIDForSelectedRowIndexes = (
    selectedRowIndexes: any,
    records: any
  ) => {
    return _.map(selectedRowIndexes, (index: number) => {
      let correspondingCommitteeAssignment = records[index];
      return correspondingCommitteeAssignment.attendeeId;
    });
  };

  const formatObjectForCommitteeReassignment = () => {
    let newCommitteeAssignmentIds = findCorrespondingAttendeeIDForSelectedRowIndexes(
      selectedRowIndexes,
      committeeAssignments
    );
    let obj = {};
    if (bulkActionSelection === "moveMember") {
      if (committeeDropDownSelectionBulkAction !== "") {
        obj = {
          OldCommittee: committeeDropDownSelection,
          NewCommittee: committeeDropDownSelectionBulkAction,
          CommitteeAssignments: newCommitteeAssignmentIds,
        };
      }
    } else {
      obj = {
        OldCommittee: committeeDropDownSelection,
        NewCommittee: "0",
        CommitteeAssignments: newCommitteeAssignmentIds,
      };
    }
    return obj;
  };

  const applyBulkActions = () => {
    let obj = formatObjectForCommitteeReassignment();
    setCommitteeAssignmentsRequestPending(true);
    setConfirmationModalOpen(false);
    axios
      .post("/api/Committees/updateMembersCommitteeAssignment", obj, {
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
      })
      .then((response) => {
        if (response && response.data) {
          const { data } = response;
          setCommitteeAssignments(data.committeeAssignments);
          updateCurrentCommitteeType(data.currentCommittee);
          setSelectedRowIndexes([]);
          setBulkActionSelection("");
          enqueueSnackbar(
            `The selected members have been ${
              bulkActionSelection === "moveMember" ? "moved" : "removed"
            }!`,
            {
              variant: "success",
            }
          );
        }
        setCommitteeAssignmentsRequestPending(false);
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          console.warn("Error: " + error.message);
        } else {
          console.warn(error);
          enqueueSnackbar("The following error occured: " + error, {
            variant: "error",
          });
          setCommitteeAssignmentsRequestPending(false);
        }
      });
  };

  // Add memeber to committee data table checkboxes configs
  const [selectedRowIndexesAddMember, setSelectedRowKeysAddMember] = useState(
    []
  );

  const [addMemberRequestPending, setAddMemberRequestPending] = React.useState(
    false
  );

  const addMembersFromMemberSearchToSpecialCommittees = () => {
    let attendeeIds = findCorrespondingAttendeeIDForSelectedRowIndexes(
      selectedRowIndexesAddMember,
      memberSearchResult
    );
    handleClose();
    setAddMemberRequestPending(true);
    axios
      .post(
        "/api/Committees/AddCommitteeAssignments",
        {
          CommitteeAssignmentId: committeeDropDownSelection,
          attendeeIds: attendeeIds,
        },
        {
          cancelToken: new CancelToken(function executor(c) {
            // An executor function receives a cancel function as a parameter
            cancel = c;
          }),
        }
      )
      .then((response) => {
        const { data } = response;
        setCommitteeAssignments(data.committeeAssignments);
        updateCurrentCommitteeType(data.currentCommittee);
        setAddMemberRequestPending(false);
        enqueueSnackbar("The members you selected have been added!", {
          variant: "success",
        });
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          console.warn("Error: " + error.message);
        } else {
          console.warn(error);
          setAddMemberRequestPending(false);
          enqueueSnackbar("The following error occured: " + error, {
            variant: "error",
          });
        }
      });
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedRowKeysAddMember([]);
  };

  return (
    <Grid container size={{ xs: 12 }}>
      <Grid size={{ xs: 12 }}>
        <h2>Member Committee Search</h2>
      </Grid>
      <Grid container size={{ xs: 12 }}>
        {committeeAssignmentsRequestPending || addMemberRequestPending ? (
          <Grid
            container
            size={{ xs: 12 }}
            direction="row"
            justifyContent="center"
            alignItems="center"
            className="pt-5"
          >
            <Grid size={{ xs: 12 }}>
              <GenericSpinner includeOverlay={false} padding={0} />
            </Grid>
            <Grid size={{ xs: 12 }} className="pt-3 text-center">
              <span>
                Loading{" "}
                {currentSelectedCommittee &&
                  (currentSelectedCommittee as any).committeeName}
                ...
              </span>
            </Grid>
          </Grid>
        ) : (
          <Grid container size={{ xs: 12 }}>
            {initialLoadPending ? (
              <Grid
                container
                size={{ xs: 12 }}
                direction="row"
                justifyContent="center"
                alignItems="center"
                className="pt-5"
              >
                <Grid size={{ xs: 12 }}>
                  <GenericSpinner includeOverlay={false} padding={0} />
                </Grid>
                <Grid size={{ xs: 12 }} className="pt-3 text-center">
                  <span>Loading Committees...</span>
                </Grid>
              </Grid>
            ) : (
              <Grid container size={{ xs: 12 }}>
                <Grid size={{ xs: 12, md: 3 }} className="pt-2">
                  <Dropdown
                    id={"committee-selection-dropdown"}
                    onChange={handleCommitteeTypeDropdownChange}
                    selectedPropsValue={committeeDropDownSelection}
                    label={"Committees"}
                    values={(committeeTypes as (committeeTypesItem | string)[]).map(
                      (item: committeeTypesItem | string, index: number) => {
                        const typedItem = item as committeeTypesItem;
                        let value = index === 0 ? "" : typedItem.committeeTypeId;
                        let displayValue =
                          index === 0 ? item : typedItem.committeeName;
                        let isSpecialCommittee =
                          index === 0 ? "" : typedItem.isSpecialCommittee;
                        return (
                          <option key={index} value={value as string | number}>
                            {`${displayValue}${isSpecialCommittee ? "*" : ""}`}
                          </option>
                        );
                      }
                    )}
                  />
                </Grid>
              </Grid>
            )}
            {committeeDropDownSelection !== "" && (
              <Grid container size={{ xs: 12 }} className="pt-5">
                <Grid size={{ xs: 12, md: 8 }}>
                  <Grid
                    container
                    size={{ xs: 12 }}
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                  >
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Dropdown
                        id={"bulk-actions-dropdown"}
                        selectedPropsValue={bulkActionSelection}
                        onChange={bulkActionOnChange}
                        label="Actions"
                        name={"bulkActionsDropdown"}
                        values={[
                          { name: "" },
                          { name: "Remove Member(s)", value: "removeMember" },
                          { name: "Move Member(s) To", value: "moveMember" },
                        ].map((item: any, index: number) => {
                          if (_.isEmpty(item.name)) {
                            return <option key={index} value={""} />;
                          }
                          return (
                            <option key={index} value={item.value}>
                              {item.name}
                            </option>
                          );
                        })}
                      />
                    </Grid>
                    {bulkActionSelection === "moveMember" && (
                      <Grid
                        size={{ xs: 12, md: 4 }}
                        className={`${displayMobile ? "pt-2" : "pl-2"}`}
                      >
                        <Dropdown
                          id={"committee-selection-bulk-action"}
                          onChange={handleCommitteeTypeDropdownChangeBulkAction}
                          selectedPropsValue={
                            committeeDropDownSelectionBulkAction
                          }
                          label="Committee"
                          values={(committeeTypes as (committeeTypesItem | string)[]).map(
                            (item: committeeTypesItem | string, index: number) => {
                              const typedItem = item as committeeTypesItem;
                              if (
                                String(typedItem.committeeTypeId) ===
                                String(committeeDropDownSelection)
                              ) {
                                return;
                              }
                              return (
                                <option
                                  key={index}
                                  value={typedItem.committeeTypeId}
                                >
                                  {typedItem.committeeName}
                                </option>
                              );
                            }
                          )}
                        />
                      </Grid>
                    )}
                    {(bulkActionSelection === "moveMember" ||
                      bulkActionSelection === "removeMember") && (
                      <Grid
                        size={{ md: 4 }}
                        className={`${displayMobile ? "pt-2" : "pl-2"}`}
                      >
                        <Button
                          type="button"
                          onClick={applyButtonOnClickModalPopup}
                          size="large"
                          variant="contained"
                          color="primary"
                          className="btn"
                        >
                          Apply
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                <Grid
                  size={{ xs: 12, md: 4 }}
                  className={`${displayMobile && "pt-2"}`}
                >
                  <Grid container size={{ xs: 12 }}>
                    <Grid
                      size={{ xs: 12 }}
                      className={`${!displayMobile && "float-right"}`}
                    >
                      {(currentSelectedCommittee as any).isAtMaxCapacity &&
                        !committeeAssignmentsRequestPending && (
                          <Alert severity="warning">
                            This committee is at maximum capacity.
                          </Alert>
                        )}
                    </Grid>
                  </Grid>
                </Grid>
                <Grid
                  container
                  size={{ xs: 12 }}
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="center"
                  className="pt-4"
                >
                  <Grid size={{ xs: 12, md: 6 }}>
                    <span>
                      There were{" "}
                      {committeeAssignments && committeeAssignments.length}{" "}
                      results found for "
                      {currentSelectedCommittee &&
                        (currentSelectedCommittee as any).committeeName}
                      "
                    </span>
                  </Grid>
                  <Grid
                    size={{ xs: 12, md: 6 }}
                    className={`${displayMobile && "pt-3"}`}
                  >
                    <Button
                      type="button"
                      onClick={() => setOpen(true)}
                      size="large"
                      className={`btn ${!displayMobile && "float-right"} btn`}
                      fullWidth={displayMobile ? true : false}
                      variant="contained"
                      color="primary"
                    >
                      Add Member(s)
                    </Button>
                  </Grid>
                </Grid>
                <Grid size={{ xs: 12 }} className="w-100 pt-3">
                  <MUIDataTableWrapper
                    setExpandableRowContent={(rowData: any) => {
                      return (
                        <TableRow>
                          <TableCell />
                          <TableCell colSpan={3}>
                            <Table>
                              <TableBody className="inner-tbody-expandable-row">
                                <TableRow>
                                  <TableCell
                                    align="left"
                                    className="tbl-col-container"
                                  >
                                    <div className="tbl-col-title">
                                      Affiliate Number
                                    </div>
                                    <div>
                                      {rowData[4]
                                        ? rowData[4].toString()
                                        : "N/A"}
                                    </div>
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell
                                    align="left"
                                    className="tbl-col-container"
                                  >
                                    <div className="tbl-col-title">
                                      Affiliate Name
                                    </div>
                                    <div>
                                      {rowData[5]
                                        ? rowData[5].toString()
                                        : "N/A"}
                                    </div>
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell
                                    align="left"
                                    className="tbl-col-container"
                                  >
                                    <div className="tbl-col-title">
                                      Checked-In
                                    </div>
                                    <div>
                                      {rowData[6]
                                        ? rowData[6].toString()
                                        : "N/A"}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableCell>
                        </TableRow>
                      );
                    }}
                    title={`${
                      currentSelectedCommittee &&
                      (currentSelectedCommittee as any).committeeName
                    } Results`}
                    columns={[
                      {
                        name: "attendeeId",
                        label: "Attendee ID",
                        options: {
                          display: "excluded",
                        },
                      },
                      {
                        label: "Member Id",
                        name: "member-id",
                      },
                      {
                        label: "First Name",
                        name: "first-name",
                      },
                      {
                        label: "Last Name",
                        name: "last-name",
                      },
                      {
                        label: "Affiliate",
                        name: "local",
                        options: {
                          display: displayMobile ? "false" : "true",
                        },
                      },
                      {
                        label: "Affiliate Name",
                        name: "local-name",
                        options: {
                          display: displayMobile ? "false" : "true",
                        },
                      },
                      {
                        label: "Affiliate State",
                        name: "affiliate-state",
                        options: {
                          display: displayMobile ? "false" : "true",
                        },
                      }
                    ]}
                    data={committeeAssignments.map((assignment: any) => {
                      return {
                        attendeeId: assignment.attendeeId,
                        "member-id": assignment.memberId,
                        "first-name": assignment.firstName,
                        "last-name": assignment.lastName,
                        local: assignment.localNumber,
                        "local-name": assignment.localName,
                        "affiliate-state": assignment.affiliateState,
                        "checked-in": assignment.checkedIn
                          ? "Checked In"
                          : "Not Checked In",
                      };
                    })}
                    options={{
                      filter: false,
                      sort: false,
                      print: false,
                      download: false,
                      viewColumns: false,
                      selectableRows: "multiple",
                      selectableRowsOnClick: true,
                      filterType: "dropdown",
                      rowsPerPageOptions: [5, 10, 15, 25],
                      rowsSelected: selectedRowIndexes,
                      onRowsSelect: (rowsSelected: any, allRows: any) => {
                        setSelectedRowIndexes(
                          allRows.map((row: any) => row.dataIndex)
                        );
                      },
                      selectableRowsHeader: false,
                      disableToolbarSelect: true,
                    }}
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
        )}
        <GenericModal
          open={open}
          onClose={handleClose}
          title={"Member Search"}
          contentWidth={displayMobile ? 100 : 65}
          content={
            <Grid container size={{ xs: 12 }}>
              <MemberSearch
                delegateOnly={true}
                hideTitle={true}
                isForAddingMembers={true}
                title={"Member Search"}
                options={{
                  filter: false,
                  sort: false,
                  print: false,
                  download: false,
                  viewColumns: false,
                  selectableRows: "multiple",
                  selectableRowsOnClick: true,
                  filterType: "dropdown",
                  rowsPerPageOptions: [5, 10, 15, 25],
                  rowsSelected: selectedRowIndexesAddMember,
                  onRowsSelect: (rowsSelected: any, allRows: any) => {
                    setSelectedRowKeysAddMember(
                      allRows.map((row: any) => row.dataIndex)
                    );
                  },
                  selectableRowsHeader: false,
                  disableToolbarSelect: true,
                }}
              />
              {memberSearchResult.length > 0 &&
                selectedRowIndexesAddMember.length > 0 && (
                  <Grid size={{ xs: 12, md: 12 }} className="pt-2">
                    <Button
                      type="button"
                      onClick={addMembersFromMemberSearchToSpecialCommittees}
                      size="large"
                      variant="contained"
                      color="primary"
                      className="btn"
                    >
                      Add Member(s)
                    </Button>
                  </Grid>
                )}
            </Grid>
          }
        />
        <ConfirmActionModal
          prompt={`Are you sure you want to ${
            bulkActionSelection === "moveMember" ? "move" : "remove"
          } this member?`}
          modalOpen={confirmationModalOpen}
          onClose={confirmActionModalOnClose}
          confirmButtonOnClick={applyBulkActions}
        />
      </Grid>
    </Grid>
  );
};

export default CommitteeManagement;
